import type {
  AcceptTermsAndConditionsRequest,
  CompleteFirstInstallOnboardingRequest,
  CompleteFirstInstallOnboardingResponse,
  Onboarding,
  TcAcceptResponse,
} from "@redotech/account-common/onboarding";
import { RedoMerchantClient } from "@redotech/redo-merchant-app-common/client";
import { sleep } from "@redotech/util/schedule";
import axios from "axios";

const LEGACY_ROLLING_DEPLOY_ATTRIBUTION_SOURCE = "other";
const PRODUCTLESS_ROLLING_DEPLOY_ATTEMPTS = 6;

export async function getCurrentOnboarding(
  client: RedoMerchantClient,
  { signal }: { signal?: AbortSignal } = {},
): Promise<Onboarding> {
  const response = await client.client.get("onboarding", {
    headers: client.authorization(),
    params: { _t: Date.now() },
    signal,
  });
  return response.data;
}

export async function onboardingTc(
  client: RedoMerchantClient,
  {
    firstInstallOnboardingVersion,
    signal,
  }: AcceptTermsAndConditionsRequest & { signal?: AbortSignal } = {},
): Promise<TcAcceptResponse> {
  const response = await client.client.put(
    "onboarding/tc",
    firstInstallOnboardingVersion ? { firstInstallOnboardingVersion } : {},
    { headers: client.authorization(), signal },
  );
  return response.data;
}

export async function completeFirstInstallOnboarding(
  client: RedoMerchantClient,
  input: CompleteFirstInstallOnboardingRequest,
  { signal }: { signal?: AbortSignal } = {},
): Promise<CompleteFirstInstallOnboardingResponse> {
  for (let attempt = 1; ; attempt += 1) {
    try {
      const response = await client.client.post(
        "onboarding/first-install/complete",
        {
          // Old server pods still require this field during a rolling deployment.
          attributionSource: LEGACY_ROLLING_DEPLOY_ATTRIBUTION_SOURCE,
          ...input,
        },
        { headers: client.authorization(), signal },
      );
      return response.data;
    } catch (error) {
      const shouldRetryAgainstNewServer =
        input.selectedProduct == null &&
        attempt < PRODUCTLESS_ROLLING_DEPLOY_ATTEMPTS &&
        axios.isAxiosError<{ error?: string }>(error) &&
        error.response?.status === 400 &&
        error.response.data.error === "Product selection is required";
      if (!shouldRetryAgainstNewServer) {
        throw error;
      }
      await sleep(
        Temporal.Duration.from({ seconds: 2 ** (attempt - 1) }),
        signal,
      );
    }
  }
}

export async function dismissFirstInstallOnboarding(
  client: RedoMerchantClient,
  { signal }: { signal?: AbortSignal } = {},
): Promise<void> {
  await client.client.post(
    "onboarding/first-install/dismiss",
    {},
    { headers: client.authorization(), signal },
  );
}
